import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { 
  LayoutDashboard, FileQuestion, ClipboardList, BarChart3, 
  Shield, LogOut, Users, Search, Activity, Globe, Clock,
  LogIn, UserPlus, PlayCircle, CheckCircle, MessageSquare
} from "lucide-react";

const IPLogs = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      const response = await axios.get(`${API}/admin/ip-logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(response.data);
    } catch (error) {
      toast.error("Failed to fetch logs");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/auth");
  };

  const navItems = [
    { icon: LayoutDashboard, label: "Dashboard", path: "/admin" },
    { icon: FileQuestion, label: "Question Bank", path: "/admin/questions" },
    { icon: ClipboardList, label: "Exams", path: "/admin/exams" },
    { icon: BarChart3, label: "Results", path: "/admin/results" },
    { icon: Users, label: "Organisers", path: "/admin/organisers" },
    { icon: MessageSquare, label: "Feedback", path: "/admin/feedback" },
    { icon: Activity, label: "IP Logs", path: "/admin/logs", active: true }
  ];

  const getActionIcon = (action) => {
    switch (action) {
      case 'login': return <LogIn className="w-4 h-4" />;
      case 'register': return <UserPlus className="w-4 h-4" />;
      case 'exam_start': return <PlayCircle className="w-4 h-4" />;
      case 'exam_submit': return <CheckCircle className="w-4 h-4" />;
      default: return <Activity className="w-4 h-4" />;
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'login': return 'bg-blue-50 text-blue-600';
      case 'register': return 'bg-emerald-50 text-emerald-600';
      case 'exam_start': return 'bg-amber-50 text-amber-600';
      case 'exam_submit': return 'bg-indigo-50 text-indigo-600';
      default: return 'bg-slate-50 text-slate-600';
    }
  };

  const filteredLogs = logs.filter(log => 
    log.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    log.ip_address?.includes(searchQuery) ||
    log.action?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="flex min-h-screen bg-slate-50" data-testid="ip-logs">
      {/* Sidebar */}
      <aside className="sidebar">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-600 to-blue-500 flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>edSolutions360</span>
              <p className="text-xs text-slate-500">Examination Portal</p>
            </div>
          </div>
          
          <nav className="space-y-2">
            {navItems.map((item) => (
              <div
                key={item.path}
                className={`nav-item ${item.active ? 'active' : ''}`}
                onClick={() => navigate(item.path)}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </div>
            ))}
          </nav>
        </div>
        
        <div className="mt-auto p-6 border-t border-slate-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
              <Users className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-900">{user?.full_name}</p>
              <p className="text-xs text-slate-500">{user?.email}</p>
            </div>
          </div>
          <Button variant="outline" className="w-full" onClick={handleLogout}>
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>IP Address Logs</h1>
              <p className="text-slate-600 mt-1">Security audit trail for all user activities</p>
            </div>
            <div className="relative w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Search by email, IP, action..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-logs-input"
              />
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card className="stats-card">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center">
                  <Activity className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total Activities</p>
                  <p className="text-2xl font-bold text-slate-900">{logs.length}</p>
                </div>
              </div>
            </Card>
            <Card className="stats-card">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center">
                  <LogIn className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Logins</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {logs.filter(l => l.action === 'login').length}
                  </p>
                </div>
              </div>
            </Card>
            <Card className="stats-card">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center">
                  <PlayCircle className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Exams Started</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {logs.filter(l => l.action === 'exam_start').length}
                  </p>
                </div>
              </div>
            </Card>
            <Card className="stats-card">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Exams Submitted</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {logs.filter(l => l.action === 'exam_submit').length}
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {/* Logs Table */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="spinner" />
            </div>
          ) : filteredLogs.length === 0 ? (
            <Card className="text-center py-12">
              <Activity className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h4 className="text-lg font-medium text-slate-900 mb-2">No Logs Found</h4>
              <p className="text-slate-500">Activity logs will appear here as users interact with the platform.</p>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Action</th>
                        <th>User</th>
                        <th>Exam</th>
                        <th>IP Address</th>
                        <th>Timestamp</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredLogs.map((log, index) => (
                        <tr key={log.id || index} data-testid={`log-row-${index}`}>
                          <td>
                            <div className="flex items-center gap-2">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${getActionColor(log.action)}`}>
                                {getActionIcon(log.action)}
                              </div>
                              <span className="font-medium capitalize">{log.action?.replace('_', ' ')}</span>
                            </div>
                          </td>
                          <td>
                            <p className="text-slate-900">{log.email}</p>
                          </td>
                          <td>
                            {log.exam_name ? (
                              <Badge variant="outline">{log.exam_name}</Badge>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </td>
                          <td>
                            <div className="flex items-center gap-2">
                              <Globe className="w-4 h-4 text-slate-400" />
                              <span className="font-mono text-sm">{log.ip_address}</span>
                            </div>
                          </td>
                          <td>
                            <div className="flex items-center gap-2 text-slate-600">
                              <Clock className="w-4 h-4" />
                              <span className="text-sm">
                                {new Date(log.timestamp).toLocaleString()}
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
};

export default IPLogs;
